import { GameConfig } from '@/config/game';
import apiRequest from '@/helpers/apiRequest';
import { getNow } from '@/utilities/dateUtils';
import { DateTime } from 'luxon';

// Bei TOTO sind die Folgerunden in der ZQ schon angelegt, bevor die laufende
// Runde ausgewertet ist: /last liefert die neueste angelegte, /last/completed
// die neueste ausgewertete Runde, aber nicht zwingend die offene Runde.
// /date/{from}/{to} ist kein Zeitfenster (liefert nur bei exaktem fromDate),
// daher werden die Runden dazwischen über /year/{yearEvent} nachgeladen.
const MAX_ROUNDS_BETWEEN = 8;

interface TotoRound {
  yearEvent: string;
  deadline: number;
  jackpotNew: number | null;
  oddsGenerationDate: number | null;
  oddsCollection: { winningClass: number; jackpot: number | null }[] | null;
}

export interface TotoJackpot {
  deadline: number;
  // null, solange die Vorgängerrunde der offenen Runde nicht ausgewertet ist
  jackpot: number | null;
  jackpotClass1: number;
  jackpotClass2: number;
}

function isRound(data: any): data is TotoRound {
  return (
    !!data &&
    typeof data.yearEvent === 'string' &&
    typeof data.deadline === 'number'
  );
}

function isEvaluated(round: TotoRound): boolean {
  return (
    round.oddsGenerationDate != null || (round.oddsCollection?.length ?? 0) > 0
  );
}

// yearEvent = ISO-Jahr + ISO-Woche, z. B. 202641; bisher gibt es jede Woche
// genau eine Runde. null, wenn das Format nicht passt.
function getYearEventsBetween(from: string, to: string): string[] | null {
  if (!/^\d{6}$/.test(from) || !/^\d{6}$/.test(to)) {
    return null;
  }
  const events: string[] = [];
  let date = DateTime.fromObject({
    weekYear: Number(from.slice(0, 4)),
    weekNumber: Number(from.slice(4)),
  });
  for (;;) {
    date = date.plus({ weeks: 1 });
    const event = date.toFormat('kkkkWW');
    if (event >= to) {
      return events;
    }
    events.push(event);
  }
}

function getClassJackpot(round: TotoRound, winningClass: number): number {
  return (
    round.oddsCollection?.find((odds) => odds.winningClass === winningClass)
      ?.jackpot ?? 0
  );
}

export async function getTotoJackpot(
  game: GameConfig
): Promise<TotoJackpot | null> {
  const lastUrl = game.jackpotSrc;
  const completedUrl = game.apiUrls?.completed;
  const yearEventUrl = game.apiUrls?.yearEvent;
  if (!lastUrl || !completedUrl || !yearEventUrl) {
    return null;
  }

  const [last, completed] = await Promise.all([
    apiRequest(lastUrl),
    apiRequest(completedUrl),
  ]);
  if (!isRound(last) || !isRound(completed)) {
    return null;
  }

  const events = getYearEventsBetween(completed.yearEvent, last.yearEvent);
  const between = await Promise.all(
    (events ?? [])
      .slice(0, MAX_ROUNDS_BETWEEN)
      .map((event) => apiRequest(`${yearEventUrl}/${event}`))
  );
  // Wochen, deren Runde nicht geladen werden konnte (Fehler, Lücke, Limit)
  const unresolved = (events ?? []).filter((event, index) => {
    const round = between[index];
    return !isRound(round) || round.yearEvent !== event;
  });
  const rounds = [completed, ...between, last]
    .filter(isRound)
    .sort((a, b) => a.deadline - b.deadline);

  // maßgeblich ist deadline, da Verlängerungen dort greifen
  const now = getNow().toMillis();
  const open = rounds.find((round) => round.deadline > now);
  if (!open) {
    return null;
  }

  const previous = rounds
    .filter((round) => round.deadline < open.deadline)
    .pop();
  // Ist nicht sicher, welche Runde direkt vor der offenen liegt, wird nicht
  // auf den letzten ausgewerteten Stand zurückgefallen
  const previousUnknown =
    events === null || unresolved.some((event) => event < open.yearEvent);
  if (!previous || previousUnknown || !isEvaluated(previous)) {
    // Annahme läuft schon auf die Folgerunde, der Jackpot hängt aber noch
    // von der Auswertung der Vorgängerrunde ab
    return {
      deadline: open.deadline,
      jackpot: null,
      jackpotClass1: 0,
      jackpotClass2: 0,
    };
  }

  return {
    deadline: open.deadline,
    jackpot: previous.jackpotNew,
    jackpotClass1: getClassJackpot(previous, 1),
    jackpotClass2: getClassJackpot(previous, 2),
  };
}
